<script setup>
import { styles } from "@Utils/persona.js";
import Step from "@Components/Stepper/Step.vue";
import { useI18n } from "vue-i18n";
import { FormInput, FormCard, FormLabel, FormTextarea, FormListbox } from "@Components/Form";
import LoadingAnimation from "@Components/Animation/Loading";
import { SecondaryButton } from "@Components/Button";
import { MagicIcon } from "@Icons/index";
import { onMounted } from "vue";
import { fakerFR as faker } from "@faker-js/faker";
import { fistNames, lastNames, jobs } from "@/faker";
import { ArrowPathIcon } from "@heroicons/vue/20/solid";

const props = defineProps(["modelValue", "step"]);
defineEmits(["update:modelValue", "next", "back", "skip"]);

const { t: __ } = useI18n();

const setFakeFirstName = () => {
    props.modelValue.first_name = fistNames[Math.floor(Math.random() * fistNames.length)];
};

const setFakeLastName = () => {
    props.modelValue.last_name = lastNames[Math.floor(Math.random() * lastNames.length)];
};

const setFakeAge = () => {
    props.modelValue.age = faker.number.int({ min: 25, max: 50 });
};

const setFakeJob = () => {
    props.modelValue.job = jobs[Math.floor(Math.random() * jobs.length)];
};

const setFakeWritingStyle = () => {
    props.modelValue.writing_style = styles[Math.floor(Math.random() * styles.length)].key;
};

const generateBiography = async () => {
    if (props.modelValue.processing) return;

    const response = await props.modelValue.post("personas_generate_biography");

    props.modelValue.description = response.description;
};

onMounted(() => {
    if (props.modelValue.first_name.length <= 0) {
        setFakeFirstName();
    }
    if (props.modelValue.last_name.length <= 0) {
        setFakeLastName();
    }
    if (props.modelValue.age.length <= 0) {
        setFakeAge();
    }
    if (props.modelValue.job.length <= 0) {
        setFakeJob();
    }
    if (props.modelValue.writing_style === undefined) {
        setFakeWritingStyle();
    }
    if (props.modelValue.description.length <= 0) {
        generateBiography();
    }
});
</script>

<template>
    <Step :step="step" @next="$emit('next')" @back="$emit('back')" @skip="$emit('skip')">
        <template #header>
            <div class="mx-auto max-w-3xl">
                <h3 class="text-4xl font-semibold leading-tight text-center text-gray-900 dark:text-white">
                    {{ __("Créez le profil") }}
                </h3>
            </div>
        </template>
        <div class="mx-auto max-w-4xl flex flex-col space-y-6">
            <FormCard>
                <div class="grid gap-6 sm:grid-cols-8">
                    <div class="sm:col-span-3">
                        <FormInput :label="__('Prénom')" v-model="modelValue.first_name" :errors="modelValue.errors.get('first_name')" required class="pr-8">
                            <template #action>
                                <div class="absolute inset-y-0 right-0 flex items-center mr-2 select-none">
                                    <ArrowPathIcon
                                        class="w-4 h-4 text-gray-400 cursor-pointer dark:text-gray-300 dark:hover:text-white hover:text-gray-600"
                                        @click="setFakeFirstName" />
                                </div>
                            </template>
                        </FormInput>
                    </div>
                    <div class="sm:col-span-3">
                        <FormInput :label="__('Nom')" v-model="modelValue.last_name" :errors="modelValue.errors.get('last_name')" class="pr-8">
                            <template #action>
                                <div class="absolute inset-y-0 right-0 flex items-center mr-2 select-none">
                                    <ArrowPathIcon
                                        class="w-4 h-4 text-gray-400 cursor-pointer dark:text-gray-300 dark:hover:text-white hover:text-gray-600"
                                        @click="setFakeLastName" />
                                </div>
                            </template>
                        </FormInput>
                    </div>
                    <div class="sm:col-span-2">
                        <FormInput
                            :label="__('Âge')"
                            v-model="modelValue.age"
                            type="number"
                            min="18"
                            max="99"
                            :errors="modelValue.errors.get('age')"
                            required />
                    </div>
                    <div class="sm:col-span-5">
                        <FormInput
                            :label="__('Profession, passion ou loisir')"
                            v-model="modelValue.job"
                            :errors="modelValue.errors.get('job')"
                            required
                            class="pr-8">
                            <template #action>
                                <div class="absolute inset-y-0 right-0 flex items-center mr-2 select-none">
                                    <ArrowPathIcon
                                        class="w-4 h-4 text-gray-400 cursor-pointer dark:text-gray-300 dark:hover:text-white hover:text-gray-600"
                                        @click="setFakeJob" />
                                </div>
                            </template>
                        </FormInput>
                    </div>
                    <div class="sm:col-span-3">
                        <FormListbox
                            :label="__('Style')"
                            v-model="modelValue.writing_style"
                            :items="styles"
                            item-key="key"
                            item-display="name"
                            required
                            :errors="modelValue.errors.get('writing_style')" />
                    </div>

                    <div class="col-span-full">
                        <div class="flex justify-between items-center">
                            <FormLabel for="personas-biography">{{ __("Biographie") }}</FormLabel>
                            <SecondaryButton @click="generateBiography" :processing="modelValue.processing" :icon="MagicIcon">
                                {{ __("Générer") }}
                            </SecondaryButton>
                        </div>
                        <div class="relative">
                            <FormTextarea
                                class="mt-2"
                                id="personas-biography"
                                v-model="modelValue.description"
                                :readonly="modelValue.processing"
                                rows="10"
                                :errors="modelValue.errors.get('description')" />
                            <div
                                v-if="modelValue.processing"
                                class="bg-opacity-60 absolute inset-0 flex justify-center items-center bg-white animate-pulse dark:bg-gray-700">
                                <LoadingAnimation class="w-16 h-16" />
                            </div>
                        </div>
                    </div>
                </div>
            </FormCard>
        </div>
    </Step>
</template>
